<template>
	<div class="banners">
		<div>
			<PageHeader :title="title">
				<div>
					<Button :theme-color="'primary'" @click="handleNewBanner">Создать</Button>
				</div>
			</PageHeader>
			<p class="banners__description">Создавайте и управляйте баннерами для раздела "Спецпредложения"</p>
		</div>

		<div class="banners__filters">
			<div class="banners__filter">
				<div class="form-group">
					<LabelRequired>
						Название
					</LabelRequired>
					<div class="form-option-required-wrap">
						<Input v-model="filter.name" placeholder="Введите название баннера" @input="onInputName" />
					</div>
				</div>
			</div>

			<div class="banners__statuses">
				<button class="banners__status all" :class="{ 'active': filter.status === 'all' }"
					@click="showStatuses('all')">
					Все
				</button>
				<button class="banners__status is-active" :class="{ 'active': filter.status === 'isActive' }"
					@click="showStatuses('isActive')">
					Активные
				</button>
				<button class="banners__status disactive" :class="{ 'active': filter.status === 'disactive' }"
					@click="showStatuses('disactive')">
					Неактивные
				</button>
			</div>
		</div>

		<VLoader v-if="isLoading" />
		<div v-else>
			<div class="banners__wrap-table">
				<table class="custom-table">
					<thead>
						<tr>
							<td></td>
							<th>Превью</th>
							<th>Название</th>
							<th>Тип</th>
							<th>Ссылка на</th>
							<th>Статус</th>
							<th>Порядок</th>
							<th>Действия</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(banner, index) in filteredBanners" :key="banner.id" :class="{
							'dragging': draggedItem?.id === banner.id,
							'drag-over': dragOverItem?.id === banner.id
						}" draggable="true" @dragstart="onDragStart($event, banner)" @dragover.prevent="onDragOver($event, banner)"
							@dragenter.prevent="onDragEnter($event, banner)" @dragleave="onDragLeave($event, banner)"
							@drop="onDrop($event, banner)" @dragend="onDragEnd">
							<td>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
									stroke-width="2">
									<circle cx="9" cy="6" r="1"></circle>
									<circle cx="9" cy="12" r="1"></circle>
									<circle cx="9" cy="18" r="1"></circle>
									<circle cx="15" cy="6" r="1"></circle>
									<circle cx="15" cy="12" r="1"></circle>
									<circle cx="15" cy="18" r="1"></circle>
								</svg>
							</td>
							<td>
								<img v-if="banner?.image?.preview" :src="banner?.image?.preview" alt=""
									class="banner-img-preview" />
							</td>
							<td>{{ banner.name }}</td>
							<td>
								{{ getBannerType(banner.bannerable_type) }}
							</td>

							<td>
								{{ banner.link }}
							</td>

							<td>
								<Checkbox :checked="banner.visible" />
							</td>

							<td>
								<div class="banners__block-order">
									{{ index + 1 }}
								</div>
							</td>

							<td>
								<div class="banners__table-control">
									<Button title="Редактировать" :theme-color="'primary'" :svg-icon="pencilIcon"
										@click="edit(banner)">
									</Button>
									<Button :svg-icon="trashIcon" @click="showModalDelete(banner)" />
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<ModalDelete v-if="isShowModalDelete" @accept="modalDeleteAccept" @cancel="modalDeleteCancel" />
	</div>
</template>

<script setup>
import { ref, onMounted, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAppStore } from "@/stores/app";

import { bannersTypes } from '@/constants/banners'


import api from '@/api'

import VLoader from "@/ui-kit/components/VLoader";
import PageHeader from "@/components/page/PageHeader.vue";
import { Button } from '@progress/kendo-vue-buttons';
import { Input } from "@progress/kendo-vue-inputs";
import LabelRequired from "@/components/forms/LabelRequired.vue";
import { pencilIcon, trashIcon } from "@progress/kendo-svg-icons";
import { Checkbox } from "@progress/kendo-vue-inputs";
import ModalDelete from "@/components/modals/ModalDelete.vue";

const router = useRouter();
const appStore = useAppStore();

const title = 'Баннеры';

const banners = ref([]);
const isLoading = ref(true);
const filter = reactive({
	name: '',
	status: 'all'
})
const draggedItem = ref(null);
const dragOverItem = ref(null);
const isShowModalDelete = ref(false);
const bannerDelete = ref(null);


const filteredBanners = computed(() => {
	let result = banners.value;

	if (filter.name) {
		const searchTerm = filter.name.toLowerCase();
		result = result.filter(banner => banner.name.toLowerCase().includes(searchTerm));
	}

	if (filter.status === 'isActive') {
		result = result.filter(banner => banner.visible);
	} else if (filter.status === 'disactive') {
		result = result.filter(banner => !banner.visible);
	}

	return result;
})

const modalDeleteCancel = () => {
	isShowModalDelete.value = false;
}

const modalDeleteAccept = () => {
	isShowModalDelete.value = false;
	api.banner.remove(bannerDelete.value.id);
	appStore.showNotification('Успешно удален баннер', 'success');
	getBanners();
}

const getBannerType = (bannerType) => {
	const findItem = bannersTypes.filter((bannerTypeItem) => {
		if (bannerType === bannerTypeItem.key) {
			return true
		}

		return false;
	})

	return findItem && findItem[0] ? findItem[0].name : ''
}

const showStatuses = (status) => {
	filter.status = status;
}

const onDragStart = (event, item) => {
	draggedItem.value = item;
	event.dataTransfer.setData('text/plain', item.id);
	event.dataTransfer.effectAllowed = 'move';

	// Визуальный эффект
	setTimeout(() => {
		event.target.classList.add('dragging');
	}, 0);
};

const onDragOver = (event, item) => {
	event.preventDefault();
	dragOverItem.value = item;
};

const onDragEnter = (event, item) => {
	if (draggedItem.value && draggedItem.value.id !== item.id) {
		dragOverItem.value = item;
	}
};

const onDragLeave = (event) => {
	// Убираем подсветку только если мышь вышла за пределы строки
	if (!event.currentTarget.contains(event.relatedTarget)) {
		event.currentTarget.classList.remove('drag-over');
	}
};

const onDrop = (event, targetItem) => {
	event.preventDefault();

	if (!draggedItem.value || draggedItem.value.id === targetItem.id) return;

	// Находим индексы
	const draggedIndex = banners.value.findIndex(item => item.id === draggedItem.value.id);
	const targetIndex = banners.value.findIndex(item => item.id === targetItem.id);

	if (draggedIndex !== -1 && targetIndex !== -1) {
		// Перемещаем элемент
		const newItems = [...banners.value];
		const [movedItem] = newItems.splice(draggedIndex, 1);
		newItems.splice(targetIndex, 0, movedItem);

		// Обновляем порядок
		newItems.forEach((item, index) => {
			item.order = index + 1;
		});

		banners.value = newItems;
		saveOrder();
	}

	resetDragState();
};

const saveOrder = () => {
	try {
		const orderData = banners.value.map((banner) => banner.id);
		api.banners.updateOrder(orderData);

	} catch (err) {
		console.error(err);
	}
}

const showModalDelete = async (banner) => {
	isShowModalDelete.value = true;
	bannerDelete.value = banner;
}

const edit = (banner) => {
	console.log('edit');
	router.push(`/admin/banners/${banner.id}`);
}

const onDragEnd = () => {
	resetDragState();
};

const resetDragState = () => {
	draggedItem.value = null;
	dragOverItem.value = null;

	document.querySelectorAll('.dragging, .drag-over').forEach(el => {
		el.classList.remove('dragging', 'drag-over');
	});
};

const getBanners = async () => {
	try {
		isLoading.value = true;
		const { data } = await api.banners.get();
		banners.value = data?.data || [];

		console.log('banners', banners.value)

	} catch (err) {
		console.error(err);
		banners.value = [];

	} finally {
		isLoading.value = false;
	}
}

const handleNewBanner = () => {
	router.push('/admin/banners/new');
}

onMounted(() => {
	getBanners()
});
</script>

<style scoped>
.custom-table-container {
	font-family: Arial, sans-serif;
	max-width: 1000px;
	margin: 20px auto;
	padding: 20px;
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.controls {
	display: flex;
	gap: 15px;
	margin-bottom: 20px;
	align-items: center;
}

.search-input {
	padding: 8px 12px;
	border: 1px solid #ddd;
	border-radius: 4px;
	flex-grow: 1;
	max-width: 300px;
}

.btn {
	padding: 8px 16px;
	background: #4CAF50;
	color: white;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	transition: background 0.3s;
}

.btn:hover {
	background: #45a049;
}

.custom-table {
	width: 100%;
	border-collapse: collapse;
	margin-top: 15px;
}

.custom-table th {
	min-width: 10ch;
	background: #f8f9fa;
	padding: 12px;
	text-align: left;
	border-bottom: 2px solid #dee2e6;
	font-weight: bold;
	color: #495057;
}

.custom-table td {
	padding: 12px;
	border-bottom: 1px solid #dee2e6;
	vertical-align: middle;
}

.custom-table tbody tr {
	transition: background 0.3s, transform 0.2s;
	cursor: move;
}

.custom-table tbody tr:hover {
	background: #f8f9fa;
}

.custom-table tbody tr.dragging {
	opacity: 0.5;
	background: #e3f2fd;
}

.custom-table tbody tr.drag-over {
	border-top: 2px solid #2196F3;
	background: #f0f7ff;
}

.no-image {
	color: #999;
	font-style: italic;
}

.status {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: bold;
}

.status.active {
	background: #d4edda;
	color: #155724;
}

.status.inactive {
	background: #f8d7da;
	color: #721c24;
}

.order-cell {
	text-align: center;
}

.order-cell button {
	background: none;
	border: 1px solid #ddd;
	padding: 4px 8px;
	margin: 0 4px;
	cursor: pointer;
	border-radius: 4px;
}

.order-cell button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.order-cell button:hover:not(:disabled) {
	background: #f0f0f0;
}


/* Анимация при перетаскивании */
@keyframes pulse {
	0% {
		background: #f0f7ff;
	}

	50% {
		background: #e3f2fd;
	}

	100% {
		background: #f0f7ff;
	}
}

.custom-table tbody tr.drag-over {
	animation: pulse 1s infinite;
}
</style>

<style lang="scss" scoped>
.banners {
	display: flex;
	flex-direction: column;
	gap: 2rem;

	&__description {
		color: #999;
		padding: 0;
		margin: 0;
	}

	&__statuses {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	&__status {
		background: #fff;
		border: 1px solid #999;
		color: #000;
		border-radius: 30px;
		padding: .5rem 1rem;
		cursor: pointer;

		&.all {
			&.active {
				color: #fff;
				background: blue;
				border-color: blue;
			}
		}

		&.is-active.active {
			color: #fff;
			background: green;
			border-color: green;
		}

		&.disactive.active {
			background: #ccc;
			border-color: #ccc;
		}
	}

	&__filters {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	&__wrap-table {
		width: 100%;
		overflow-x: auto;
	}

	&__table-control {
		display: flex;
		gap: .5rem;
	}
}

.banner-img-preview {
	height: 100px;
	width: 150px;
	object-fit: cover;
}

.banners__block-order {
	background: #6C757D;
	display: inline-block;
	padding: 3px 5px;
	font-size: 12px;
	color: #fff;
	border-radius: 3px;
	vertical-align: middle;
	line-height: 20px;
}
</style>